// src/Components/Admin/AdminSubmissions.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase'; // Adjust path as needed
import { collection, getDocs, query, orderBy, doc, deleteDoc, writeBatch } from 'firebase/firestore';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx'; // For Excel export
import { saveAs } from 'file-saver'; // For saving files
import styles from './AdminSubmissions.module.css';

// Helper function to create a delay
const delay = ms => new Promise(res => setTimeout(res, ms));

// --- Modal Component for Displaying Submission Details ---
const SubmissionModal = ({ submission, onClose }) => {
  if (!submission) return null;

  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return 'N/A';
    return timestamp.toDate().toLocaleString();
  };

  const DetailItem = ({ label, value }) => (
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>{label}:</span>
      <span className={styles.detailValue}>{value || 'N/A'}</span>
    </div>
  );

  const DocumentLink = ({ label, url }) => (
    url ? (
      <a href={url} target="_blank" rel="noopener noreferrer" className={styles.modalDocLink}>
        View {label}
      </a>
    ) : null
  );

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Application Details</h2>
          <button onClick={onClose} className={styles.modalCloseButton}>×</button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.detailSection}>
            <h3>Personal Information</h3>
            <DetailItem label="Full Name" value={submission.fullName} />
            <DetailItem label="Email" value={submission.email} />
            <DetailItem label="Phone Number" value={submission.mobile} />
            <DetailItem label="Address" value={`${submission.address}, ${submission.city}, ${submission.state} - ${submission.zipcode}`} />
            <DetailItem label="Gender" value={submission.gender} />
            <DetailItem label="Category" value={submission.category} />
            <DetailItem label="Physically Handicapped" value={submission.physicallyHandicapped} />
            <DetailItem label="Aadhaar Number" value={submission.aadhaarNumber} />
          </div>

          <div className={styles.detailSection}>
            <h3>Parent's Details</h3>
            <p className={styles.subHeading}>Father's Details</p>
            <DetailItem label="Name" value={submission.parents?.father?.name} />
            <DetailItem label="Mobile" value={submission.parents?.father?.mobile} />
            <DetailItem label="Profession" value={submission.parents?.father?.profession} />
            <DetailItem label="Qualification" value={submission.parents?.father?.qualification} />
            <p className={styles.subHeading}>Mother's Details</p>
            <DetailItem label="Name" value={submission.parents?.mother?.name} />
            <DetailItem label="Mobile" value={submission.parents?.mother?.mobile} />
            <DetailItem label="Profession" value={submission.parents?.mother?.profession} />
            <DetailItem label="Qualification" value={submission.parents?.mother?.qualification} />
          </div>
          
          <div className={styles.detailSection}>
            <h3>Education Details</h3>
            <p className={styles.subHeading}>Class X</p>
            <DetailItem label="Board" value={submission.education?.class10?.board} />
            <DetailItem label="School" value={submission.education?.class10?.schoolName} />
            <DetailItem label="Roll No." value={submission.education?.class10?.rollNo} />
            <DetailItem label="Passing Year" value={submission.education?.class10?.passingYear} />
            <DetailItem label="Percentage" value={`${submission.education?.class10?.percentage}%`} />
            <p className={styles.subHeading}>Class XII</p>
            <DetailItem label="Board" value={submission.education?.class12?.board} />
            <DetailItem label="School/College" value={submission.education?.class12?.schoolName} />
            <DetailItem label="Roll No." value={submission.education?.class12?.rollNo} />
            <DetailItem label="Passing Year" value={submission.education?.class12?.passingYear} />
            <DetailItem label="Percentage" value={`${submission.education?.class12?.percentage}%`} />
          </div>

          <div className={styles.detailSection}>
            <h3>Application & Payment Status</h3>
            <DetailItem label="Submission ID" value={submission.id} />
            <DetailItem label="Submission Date" value={formatDate(submission.submissionTimestamp)} />
            <DetailItem label="Status" value={submission.status} />
            <DetailItem label="Payment ID" value={submission.paymentDetails?.paymentId} />
            <DetailItem label="Order ID" value={submission.paymentDetails?.orderId} />
            <DetailItem label="Amount Paid" value={submission.paymentDetails?.amountPaid ? `₹${submission.paymentDetails.amountPaid}` : 'N/A'} />
            <DetailItem label="Payment Date" value={formatDate(submission.paymentDetails?.paidAt)} />
          </div>
          
          <div className={styles.detailSection}>
            <h3>Uploaded Documents</h3>
            <div className={styles.modalDocContainer}>
                <DocumentLink label="Passport Photo" url={submission.uploads?.passportPhoto} />
                <DocumentLink label="Aadhaar Card" url={submission.uploads?.adharCard} />
                <DocumentLink label="10th Marksheet" url={submission.uploads?.marksheet10} />
                <DocumentLink label="12th Marksheet" url={submission.uploads?.marksheet12} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


function AdminSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null); // For modal

  const fetchSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const submissionsQuery = query(
        collection(db, "test-admision"),
        orderBy("submissionTimestamp", "desc")
      );
      const querySnapshot = await getDocs(submissionsQuery);
      const fetchedSubmissions = [];
      querySnapshot.forEach((doc) => {
        fetchedSubmissions.push({ id: doc.id, ...doc.data() });
      });
      setSubmissions(fetchedSubmissions);
    } catch (err) {
      console.error("Error fetching submissions:", err);
      setError("Failed to load submissions. Please try again later.");
      toast.error("Failed to load submissions.");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return 'N/A';
    return timestamp.toDate().toLocaleString();
  };

  // Correctly map all the new fields for Excel export
  const prepareDataForExcel = (dataToExport) => {
    return dataToExport.map(sub => ({
      "Submission ID": sub.id,
      "Submission Date": formatDate(sub.submissionTimestamp),
      "Full Name": sub.fullName,
      "Email": sub.email,
      "Mobile": sub.mobile,
      "Address": `${sub.address}, ${sub.city}, ${sub.state} - ${sub.zipcode}`,
      "Gender": sub.gender,
      "Category": sub.category,
      "Handicapped": sub.physicallyHandicapped,
      "Aadhaar Number": sub.aadhaarNumber,
      "Father's Name": sub.parents?.father?.name,
      "Father's Mobile": sub.parents?.father?.mobile,
      "Father's Profession": sub.parents?.father?.profession,
      "Father's Qualification": sub.parents?.father?.qualification,
      "Mother's Name": sub.parents?.mother?.name,
      "Mother's Mobile": sub.parents?.mother?.mobile,
      "Mother's Profession": sub.parents?.mother?.profession,
      "Mother's Qualification": sub.parents?.mother?.qualification,
      "10th Board": sub.education?.class10?.board,
      "10th School": sub.education?.class10?.schoolName,
      "10th Roll No": sub.education?.class10?.rollNo,
      "10th %": sub.education?.class10?.percentage,
      "10th Passing Year": sub.education?.class10?.passingYear,
      "12th Board": sub.education?.class12?.board,
      "12th School": sub.education?.class12?.schoolName,
      "12th Roll No": sub.education?.class12?.rollNo,
      "12th %": sub.education?.class12?.percentage,
      "12th Passing Year": sub.education?.class12?.passingYear,
      "Status": sub.status || 'Unknown',
      "Payment ID": sub.paymentDetails?.paymentId || 'N/A',
      "Order ID": sub.paymentDetails?.orderId || 'N/A',
      "Amount Paid": sub.paymentDetails?.amountPaid || 'N/A',
      "Payment Date": sub.paymentDetails?.paidAt ? formatDate(sub.paymentDetails.paidAt) : 'N/A',
      "Photo URL": sub.uploads?.passportPhoto || 'N/A',
      "Aadhar Card URL": sub.uploads?.adharCard || 'N/A',
      "10th Marksheet URL": sub.uploads?.marksheet10 || 'N/A',
      "12th Marksheet URL": sub.uploads?.marksheet12 || 'N/A',
    }));
  };

  const handleDownloadExcel = (data, fileName = "submissions.xlsx") => {
    if (!data || data.length === 0) {
      toast.warn("No data to export.");
      return;
    }
    const excelData = prepareDataForExcel(Array.isArray(data) ? data : [data]);
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Submissions");
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"});
    saveAs(blob, fileName);
    toast.success("Excel file download initiated!");
  };

  const handleDeleteSingle = async (submissionId, submissionName) => {
    if (!window.confirm(`Are you sure you want to delete the application for "${submissionName}" (ID: ${submissionId})? This action cannot be undone.`)) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, "test-admision", submissionId));
      toast.success(`Application for "${submissionName}" deleted successfully.`);
      setSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
    } catch (err) {
      console.error("Error deleting submission:", err);
      toast.error(`Failed to delete application: ${err.message}`);
    }
    setIsDeleting(false);
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("ARE YOU ABSOLUTELY SURE you want to delete ALL submitted applications? This action is irreversible and will permanently remove all data from the database.")) {
      return;
    }
    if (!window.confirm("SECOND CONFIRMATION: Deleting all applications cannot be undone. Proceed?")) {
        return;
    }
    setIsDeleting(true);
    toast.info("Deleting all submissions... This may take a moment.");
    try {
      const querySnapshot = await getDocs(collection(db, "test-admision"));
      if (querySnapshot.empty) {
        toast.warn("No documents to delete.");
        setIsDeleting(false);
        return;
      }
      const batchArray = [];
      let currentBatch = writeBatch(db);
      let operationCount = 0;

      querySnapshot.forEach((docSnapshot) => {
        currentBatch.delete(docSnapshot.ref);
        operationCount++;
        if (operationCount === 499) {
          batchArray.push(currentBatch);
          currentBatch = writeBatch(db);
          operationCount = 0;
        }
      });
      if (operationCount > 0) {
          batchArray.push(currentBatch);
      }
      for (const batch of batchArray) {
          await batch.commit();
          await delay(500);
      }
      toast.success("All applications deleted successfully.");
      setSubmissions([]);
    } catch (err) {
      console.error("Error deleting all submissions:", err);
      toast.error(`Failed to delete all applications: ${err.message}`);
    }
    setIsDeleting(false);
  };

  const handleRowClick = (submission) => {
    setSelectedSubmission(submission);
  };

  const handleCloseModal = () => {
    setSelectedSubmission(null);
  };


  if (loading) {
    return <div className={styles.adminPageWrapper}><div className={styles.loadingState}>Loading submissions...</div></div>;
  }
  if (error) {
    return <div className={styles.adminPageWrapper}><div className={styles.errorState}>{error}</div></div>;
  }

  return (
    <div className={styles.adminPageWrapper}>
      <div className={styles.adminContentBox}>
        <div className={styles.headerActions}>
            <h1 className={styles.adminHeading}>Submitted Applications ({submissions.length})</h1>
            {submissions.length > 0 && (
                <div className={styles.bulkActions}>
                    <button onClick={() => handleDownloadExcel(submissions, "all_applications.xlsx")} className={`${styles.actionButton} ${styles.downloadButton}`} disabled={isDeleting}>Download All (Excel)</button>
                    <button onClick={handleDeleteAll} className={`${styles.actionButton} ${styles.deleteAllButton}`} disabled={isDeleting || submissions.length === 0}>
                        {isDeleting ? 'Deleting...' : 'Delete All'}
                    </button>
                </div>
            )}
        </div>

        {submissions.length === 0 ? (
          <p className={styles.noSubmissions}>No applications submitted yet.</p>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.submissionsTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email / Mobile</th>
                  <th>Submission Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub, index) => (
                  <tr key={sub.id} onClick={() => handleRowClick(sub)} className={styles.tableRowClickable}>
                    <td>{index + 1}</td>
                    <td>{sub.fullName}</td>
                    <td>
                        <div>{sub.email}</div>
                        <div>{sub.mobile}</div>
                    </td>
                    <td>{formatDate(sub.submissionTimestamp)}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[sub.status?.toLowerCase().replace(/[\s-]/g, '') || 'statusunknown']}`}>
                        {sub.status || 'Unknown'}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}> {/* Prevent row click from firing */}
                        <div className={styles.rowActions}>
                            <button onClick={() => handleDownloadExcel(sub, `${sub.fullName.replace(/\s+/g, '_')}_application.xlsx`)} className={styles.smallActionButton} title="Download Excel" disabled={isDeleting}>
                                📥
                            </button>
                            <button onClick={() => handleDeleteSingle(sub.id, sub.fullName)} className={`${styles.smallActionButton} ${styles.deleteButton}`} title="Delete" disabled={isDeleting}>
                                🗑️
                            </button>
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Render the modal */}
      <SubmissionModal submission={selectedSubmission} onClose={handleCloseModal} />
    </div>
  );
}

export default AdminSubmissions;